import React from "react";
import { motion } from "framer-motion";
import { Hotel, MapPin, Map, Network, Headset } from "lucide-react";

const card = {
  hidden: { opacity: 0, y: 30 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const whiteCard =
  "bg-white rounded-2xl p-3.5 sm:p-5 shadow-[0_18px_50px_-12px_rgba(18,40,75,0.28)] ring-1 ring-[#12284B]/8";
const navyCard =
  "bg-[#17205C] rounded-2xl p-3.5 sm:p-5 shadow-[0_18px_50px_-12px_rgba(18,40,75,0.5)] ring-1 ring-white/10";

export const DEFAULT_STATS = [
  { value: "1500+", label_en: "Accommodations", label_hi: "एकोमोडेशन", active: true },
  { value: "318+", label_en: "Cities", label_hi: "शहर", active: true },
  { value: "24", label_en: "States", label_hi: "राज्य", active: true },
  { value: "", label_en: "OTAs Managed in One Place", label_hi: "एक जगह मैनेज होने वाले OTA", active: true },
  { value: "", label_en: "Online Booking Support", label_hi: "ऑनलाइन बुकिंग सपोर्ट", active: true },
];

const ICONS = [Hotel, MapPin, Map, Network, Headset];

export const HeroDashboard = ({ stats, lang = "en" }) => {
  const data = (Array.isArray(stats) && stats.length > 0)
    ? DEFAULT_STATS.map((d, i) => ({ ...d, ...(stats[i] || {}) }))
    : DEFAULT_STATS;

  const label = (i) => (lang === "hi" ? (data[i].label_hi || data[i].label_en) : data[i].label_en) || DEFAULT_STATS[i].label_en;
  const value = (i) => data[i].value ?? "";
  const show = (i) => data[i].active !== false;

  return (
    <div className="relative w-full max-w-[520px] mx-auto lg:mx-0" data-testid="hero-dashboard">
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:gap-5">
        {/* Card 1 — big number + label */}
        {show(0) && (
          <motion.div custom={0} variants={card} initial="hidden" animate="show" data-testid="dash-card-1" className={`col-span-2 ${whiteCard}`}>
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 shrink-0 rounded-xl bg-[#E84B1C]/12 flex items-center justify-center">
                {React.createElement(ICONS[0], { size: 24, className: "text-[#E84B1C]" })}
              </div>
              <div>
                <div className="font-head text-3xl font-extrabold text-[#12284B] leading-none">{value(0)}</div>
                <div className="mt-1.5 text-sm font-medium text-black/55">{label(0)}</div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Card 2 — small white */}
        {show(1) && (
          <motion.div custom={1} variants={card} initial="hidden" animate="show" data-testid="dash-card-2" className={`${whiteCard} lg:-translate-y-5`}>
            <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-[#E84B1C]/12 flex items-center justify-center mb-2 sm:mb-3">
              {React.createElement(ICONS[1], { size: 20, className: "text-[#E84B1C]" })}
            </div>
            <div className="font-head text-2xl font-extrabold text-[#12284B] leading-none">{value(1)}</div>
            <div className="mt-1.5 text-sm font-medium text-black/55">{label(1)}</div>
          </motion.div>
        )}

        {/* Card 3 — small navy */}
        {show(2) && (
          <motion.div custom={2} variants={card} initial="hidden" animate="show" data-testid="dash-card-3" className={`${navyCard} lg:translate-y-3`}>
            <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-white/10 flex items-center justify-center mb-2 sm:mb-3">
              {React.createElement(ICONS[2], { size: 20, className: "text-[#E84B1C]" })}
            </div>
            <div className="font-head text-2xl font-extrabold text-white leading-none">{value(2)}</div>
            <div className="mt-1.5 text-sm font-medium text-white/60">{label(2)}</div>
          </motion.div>
        )}

        {/* Card 4 — wide label */}
        {show(3) && (
          <motion.div custom={3} variants={card} initial="hidden" animate="show" data-testid="dash-card-4" className={`sm:col-span-2 ${whiteCard} lg:-translate-y-2`}>
            <div className="flex items-center gap-2.5 sm:gap-4">
              <div className="h-9 w-9 sm:h-12 sm:w-12 shrink-0 rounded-xl bg-[#E84B1C]/12 flex items-center justify-center">
                {React.createElement(ICONS[3], { size: 24, className: "text-[#E84B1C]" })}
              </div>
              <div className="font-head text-sm sm:text-lg font-bold text-[#12284B] leading-snug">{label(3)}</div>
            </div>
          </motion.div>
        )}

        {/* Card 5 — wide label */}
        {show(4) && (
          <motion.div custom={4} variants={card} initial="hidden" animate="show" data-testid="dash-card-5" className={`sm:col-span-2 ${whiteCard}`}>
            <div className="flex items-center gap-2.5 sm:gap-4">
              <div className="h-9 w-9 sm:h-12 sm:w-12 shrink-0 rounded-xl bg-[#E84B1C]/12 flex items-center justify-center">
                {React.createElement(ICONS[4], { size: 24, className: "text-[#E84B1C]" })}
              </div>
              <div className="font-head text-sm sm:text-lg font-bold text-[#12284B] leading-snug">{label(4)}</div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default HeroDashboard;
